import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { presupuestosApi, clientesApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';

export default function EditarPresupuestoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { empresaActual } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [presupuesto, setPresupuesto] = useState(null);
  const [clientes, setClientes] = useState([]);
  
  // Form state
  const [clienteId, setClienteId] = useState('');
  const [items, setItems] = useState([]);
  const [validezDias, setValidezDias] = useState(30);
  const [formaPago, setFormaPago] = useState('');
  const [notas, setNotas] = useState('');
  const [tipoIva, setTipoIva] = useState(21);

  useEffect(() => {
    loadData();
  }, [id, empresaActual?.id]);

  const loadData = async () => {
    if (!empresaActual?.id) return;
    
    try {
      setLoading(true);
      
      // Load presupuesto
      const presupuestoRes = await presupuestosApi.getOne(id);
      const p = presupuestoRes.data;
      
      // Check if editable
      if (!['borrador', 'pendiente'].includes(p.estado)) {
        toast.error('Este presupuesto no se puede editar');
        navigate(`/presupuestos/${id}`);
        return;
      }
      
      setPresupuesto(p);
      setClienteId(p.cliente_id);
      setItems(p.items.map(item => ({
        descripcion: item.descripcion,
        cantidad: item.cantidad,
        precio_unitario: item.precio_unitario,
        precio_con_iva: (item.precio_unitario * 1.21).toFixed(2),
        descuento_tipo: item.descuento_tipo || null,
        descuento_valor: item.descuento_valor || 0
      })));
      setValidezDias(p.validez_dias || 30);
      setFormaPago(p.forma_pago || '');
      setNotas(p.notas || '');
      
      // Load clientes
      const clientesRes = await clientesApi.getAll(empresaActual.id);
      setClientes(clientesRes.data);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar el presupuesto');
      navigate('/presupuestos');
    } finally {
      setLoading(false);
    }
  };

  const calcularItemTotal = (item) => {
    const precio = parseFloat(item.precio_unitario) || 0;
    const cantidad = parseFloat(item.cantidad) || 0;
    const subtotalItem = cantidad * precio;
    let descuentoAplicado = 0;
    
    if (item.descuento_tipo && item.descuento_valor > 0) {
      if (item.descuento_tipo === 'porcentaje') {
        descuentoAplicado = subtotalItem * (item.descuento_valor / 100);
      } else if (item.descuento_tipo === 'fijo') {
        descuentoAplicado = Math.min(item.descuento_valor, subtotalItem);
      }
    }
    
    return {
      subtotalItem,
      descuentoAplicado,
      total: Math.max(0, subtotalItem - descuentoAplicado)
    };
  };

  const calcularTotales = () => {
    let subtotal = 0;
    let totalDescuentos = 0;
    
    items.forEach(item => {
      const calc = calcularItemTotal(item);
      subtotal += calc.total;
      totalDescuentos += calc.descuentoAplicado;
    });
    
    const ivaRate = tipoIva / 100;
    const iva = subtotal * ivaRate;
    const total = subtotal + iva;
    return { subtotal, iva, total, totalDescuentos };
  };

  const calcularPrecioConIva = (precioSinIva) => {
    const precio = parseFloat(precioSinIva) || 0;
    return (precio * (1 + tipoIva / 100)).toFixed(2);
  };

  const calcularPrecioSinIva = (precioConIva) => {
    const precio = parseFloat(precioConIva) || 0;
    return (precio / (1 + tipoIva / 100)).toFixed(2);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    if (field === 'precio_unitario' && value !== '') {
      newItems[index].precio_con_iva = calcularPrecioConIva(value);
    } else if (field === 'precio_con_iva' && value !== '') {
      newItems[index].precio_unitario = calcularPrecioSinIva(value);
    }
    
    if (field === 'descuento_tipo' && value === 'none') {
      newItems[index].descuento_tipo = null;
      newItems[index].descuento_valor = 0;
    }
    
    setItems(newItems);
  };

  const handleAddItem = () => {
    setItems([...items, { 
      descripcion: '', 
      cantidad: 1, 
      precio_unitario: '', 
      precio_con_iva: '', 
      descuento_tipo: null, 
      descuento_valor: 0 
    }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Validations
    if (!clienteId) {
      toast.error('Selecciona un cliente');
      return;
    }
    
    if (!items.every(item => item.descripcion && item.cantidad > 0 && parseFloat(item.precio_unitario) >= 0)) {
      toast.error('Completa todos los campos de los items');
      return;
    }
    
    if (!formaPago) {
      toast.error('Selecciona una forma de pago');
      return;
    }

    try {
      setSaving(true);
      
      const updateData = {
        empresa_id: empresaActual.id,
        cliente_id: clienteId,
        items: items.map(item => {
          const calc = calcularItemTotal(item);
          return {
            descripcion: item.descripcion,
            cantidad: parseFloat(item.cantidad) || 0,
            precio_unitario: parseFloat(item.precio_unitario) || 0,
            descuento_tipo: item.descuento_tipo,
            descuento_valor: parseFloat(item.descuento_valor) || 0,
            total: calc.total
          };
        }),
        validez_dias: validezDias,
        forma_pago: formaPago,
        notas: notas || null
      };
      
      await presupuestosApi.update(id, updateData);
      toast.success('Presupuesto actualizado correctamente');
      navigate(`/presupuestos/${id}`);
      
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const { subtotal, iva, total, totalDescuentos } = calcularTotales();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`/presupuestos/${id}`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Editar Presupuesto
              </h1>
              <p className="text-slate-500 text-sm">
                {presupuesto?.numero}
              </p>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-presupuesto-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Cliente */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cliente</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={clienteId} onValueChange={setClienteId}>
                  <SelectTrigger data-testid="cliente-select">
                    <SelectValue placeholder="Selecciona un cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map(cliente => (
                      <SelectItem key={cliente.id} value={cliente.id}>
                        {cliente.nombre} {cliente.empresa_cliente ? `(${cliente.empresa_cliente})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Items */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Conceptos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {items.map((item, index) => {
                  const itemCalc = calcularItemTotal(item);
                  return (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-12 md:col-span-7 space-y-2">
                          <Label>Descripción *</Label>
                          <Input
                            value={item.descripcion}
                            onChange={(e) => handleItemChange(index, 'descripcion', e.target.value)}
                            placeholder="Descripción del concepto"
                          />
                        </div>
                        <div className="col-span-4 md:col-span-2 space-y-2">
                          <Label>Cantidad *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.cantidad}
                            onChange={(e) => handleItemChange(index, 'cantidad', e.target.value)}
                            onFocus={(e) => e.target.select()}
                          />
                        </div>
                        <div className="col-span-6 md:col-span-2 text-right">
                          <Label className="block mb-2">Total</Label>
                          <span className="font-medium">{formatCurrency(itemCalc.total)}</span>
                        </div>
                        <div className="col-span-2 md:col-span-1 flex justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(index)}
                            disabled={items.length === 1}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Precios */}
                      <div className="grid grid-cols-12 gap-4 items-end pt-2 border-t border-slate-200">
                        <div className="col-span-6 md:col-span-3 space-y-2">
                          <Label className="text-xs text-slate-500">Precio sin IVA *</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={item.precio_unitario}
                            onChange={(e) => handleItemChange(index, 'precio_unitario', e.target.value)}
                            onFocus={(e) => e.target.select()}
                          />
                        </div>
                        <div className="col-span-6 md:col-span-3 space-y-2">
                          <Label className="text-xs text-slate-500">Precio con IVA ({tipoIva}%)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={item.precio_con_iva}
                            onChange={(e) => handleItemChange(index, 'precio_con_iva', e.target.value)}
                            onFocus={(e) => e.target.select()}
                            className="bg-blue-50"
                          />
                        </div>
                        <div className="col-span-12 md:col-span-6">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-xs text-slate-500">Tipo de Descuento</Label>
                              <Select 
                                value={item.descuento_tipo || 'none'} 
                                onValueChange={(val) => handleItemChange(index, 'descuento_tipo', val)}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Sin descuento" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Sin descuento</SelectItem>
                                  <SelectItem value="porcentaje">Porcentaje (%)</SelectItem>
                                  <SelectItem value="fijo">Fijo (€)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {item.descuento_tipo && item.descuento_tipo !== 'none' && (
                              <div className="space-y-2">
                                <Label className="text-xs text-slate-500">
                                  {item.descuento_tipo === 'porcentaje' ? 'Descuento (%)' : 'Descuento (€)'}
                                </Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step={item.descuento_tipo === 'porcentaje' ? '1' : '0.01'}
                                  max={item.descuento_tipo === 'porcentaje' ? '100' : undefined}
                                  value={item.descuento_valor}
                                  onChange={(e) => handleItemChange(index, 'descuento_valor', e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  className="h-9"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {itemCalc.descuentoAplicado > 0 && (
                        <div className="flex items-center gap-2 text-sm pt-2">
                          <span className="text-emerald-600 font-medium">
                            -{formatCurrency(itemCalc.descuentoAplicado)} descuento aplicado
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                <Button variant="outline" onClick={handleAddItem}>
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir concepto
                </Button>
                
                {/* IVA selector */}
                <div className="flex items-center gap-4 text-sm text-slate-600">
                  <Label className="text-slate-500">Tipo de IVA:</Label>
                  <Select value={tipoIva.toString()} onValueChange={(val) => setTipoIva(parseFloat(val))}>
                    <SelectTrigger className="w-24 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="21">21%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="4">4%</SelectItem>
                      <SelectItem value="0">0%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Condiciones */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Condiciones</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Validez (días)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={validezDias}
                      onChange={(e) => setValidezDias(parseInt(e.target.value) || 30)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Forma de pago *</Label>
                    <Select value={formaPago} onValueChange={setFormaPago}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona forma de pago" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Transferencia bancaria">Transferencia bancaria</SelectItem>
                        <SelectItem value="Contado">Contado</SelectItem>
                        <SelectItem value="Efectivo">Efectivo</SelectItem>
                        <SelectItem value="Tarjeta de crédito">Tarjeta de crédito</SelectItem>
                        <SelectItem value="50% inicio, 50% entrega">50% inicio, 50% entrega</SelectItem>
                        <SelectItem value="Pago a 30 días">Pago a 30 días</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notas (opcional)</Label>
                  <Textarea
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    placeholder="Notas adicionales para el cliente..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Resumen */}
          <div className="space-y-6">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="text-lg">Resumen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  {totalDescuentos > 0 && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Descuentos</span>
                      <span>-{formatCurrency(totalDescuentos)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-600">
                    <span>IVA ({tipoIva}%)</span>
                    <span>{formatCurrency(iva)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t">
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>
                
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
